import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useCarTaskApi } from "../../http/carTasks/api";
import ModalDialog from "../ui/ModalDialog";

import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import CarTaskForm, { CarTaskFormBody } from "./CarTaskForm";

type EditCarTaskDialogProps = {
  carTaskId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
};

const EditCarTaskDialog: FC<EditCarTaskDialogProps> = ({ carTaskId, onClose, carId }) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const carTasksApi = useCarTaskApi();
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const carTaskSwr = carTasksApi.getAdminCarTask(carTaskId, aborter.current.signal);
  const [carTask, isInitialized] = useDataInitializer(carTaskSwr.data, carTaskSwr.error);

  useEffect(() => {
    carTaskId && carTaskSwr.trigger();
  }, [carTaskId]);

  useEffect(() => {
    carTaskSwr.error && dispatch(showErrorNotification(i18n, carTaskSwr.error));
  }, [carTaskSwr.error]);

  const submit = async (body: CarTaskFormBody): Promise<void> => {
    if (body.name && carTask?.id) {
      setIsSubmitting(true);
      try {
        await carTasksApi.updateAdminCarTask(carTask, { taskName: body.name });
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog title={i18n.t.tasks.rename} onClose={() => onClose()}>
      <CarTaskForm
        carTask={carTask}
        onSubmit={submit}
        isDisabled={isSubmitting || userSettings.permissions.carTasks.update === false || !carTask?.id}
        isLoading={!isInitialized}
        carId={carId}
      />
    </ModalDialog>
  );
};

export default EditCarTaskDialog;
